import { useState } from "react";
import {
  Aviso,
  Cabecalho,
  Campo,
  Celula,
  CelulaCabecalho,
  Cartao,
  Corpo,
  Linha,
  Tabela,
} from "../ui";
import { mensagemDoConsole } from "../../nucleo/console";
import { nomeDaOrganizacao, useOrganizacoesDoCadastro } from "../../nucleo/cadastro";
import {
  ROTULO_DO_TIPO_DE_PESQUISA,
  barrasDaDistribuicao,
  faixaDoNps,
  indicadorLegivel,
  taxaLegivel,
  useResultadosDasPesquisas,
  type ResultadoDaPesquisa,
} from "../../nucleo/pesquisas";

function dataHora(valor: string): string {
  const data = new Date(valor);
  return Number.isNaN(data.getTime())
    ? "—"
    : new Intl.DateTimeFormat("pt-BR", { dateStyle: "short" }).format(data);
}

/** O painel de resultados: o indicador de cada pesquisa, a distribuição, o mês a mês e o que disseram. */
export function PainelDeResultados() {
  const [organizacaoId, setOrganizacaoId] = useState("");
  const [desde, setDesde] = useState("");
  const [ate, setAte] = useState("");
  const organizacoes = useOrganizacoesDoCadastro();
  const resultados = useResultadosDasPesquisas({ organizacaoId, desde, ate });

  return (
    <div data-testid="painel-de-resultados">
      <div className="grid gap-3 md:grid-cols-3 mb-4">
        <div className="campo">
          <label className="campo__rotulo" htmlFor="resultado-por-organizacao">
            Organização
          </label>
          <select
            id="resultado-por-organizacao"
            className="campo__controle"
            value={organizacaoId}
            onChange={(e) => setOrganizacaoId(e.target.value)}
          >
            <option value="">Todas</option>
            {(organizacoes.data ?? []).map((item) => (
              <option key={item.id} value={item.id}>
                {nomeDaOrganizacao(item)}
              </option>
            ))}
          </select>
        </div>
        <Campo rotulo="De" type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
        <Campo rotulo="Até" type="date" value={ate} onChange={(e) => setAte(e.target.value)} />
      </div>
      {resultados.isPending ? <p role="status">Calculando resultados…</p> : null}
      {resultados.isError ? <Aviso tom="erro">{mensagemDoConsole(resultados.error)}</Aviso> : null}
      {resultados.data && resultados.data.pesquisas.length === 0 ? (
        <Aviso tom="marca">Nenhuma pesquisa configurada ainda.</Aviso>
      ) : null}
      {resultados.data?.pesquisas.map((resultado) => (
        <ResultadoDeUmaPesquisa key={resultado.pesquisa_id} resultado={resultado} />
      ))}
    </div>
  );
}

function ResultadoDeUmaPesquisa({ resultado }: { resultado: ResultadoDaPesquisa }) {
  const barras = barrasDaDistribuicao(resultado.tipo, resultado.distribuicao);
  return (
    <Cartao
      titulo={`${resultado.nome} · ${ROTULO_DO_TIPO_DE_PESQUISA[resultado.tipo]}`}
      className="mb-4"
      data-testid={`resultado-${resultado.nome}`}
    >
      <div className="grid gap-4 md:grid-cols-4 mb-3">
        <Numero
          rotulo={resultado.tipo === "nps" ? "NPS" : "CES (média de 1 a 7)"}
          valor={indicadorLegivel(resultado.tipo, resultado.indicador)}
          detalhe={resultado.tipo === "nps" ? faixaDoNps(resultado.indicador) : undefined}
          testid="indicador"
        />
        <Numero rotulo="Enviados" valor={String(resultado.enviados)} testid="enviados" />
        <Numero rotulo="Respondidos" valor={String(resultado.respondidos)} testid="respondidos" />
        <Numero rotulo="Taxa de resposta" valor={taxaLegivel(resultado.taxa_de_resposta)} testid="taxa" />
      </div>
      {resultado.tipo === "nps" ? (
        <p className="mb-3" data-testid="faixas-do-nps">
          Promotores (9-10): <b>{resultado.promotores}</b> · Neutros (7-8): <b>{resultado.neutros}</b> ·
          Detratores (0-6): <b>{resultado.detratores}</b>
        </p>
      ) : null}

      <h3 className="titulo-secao">Distribuição das notas</h3>
      <ul className="mb-3" aria-label="Distribuição das notas">
        {barras.map((barra) => (
          <li key={barra.nota} className="flex items-center gap-2" data-testid={`nota-${barra.nota}`}>
            <span style={{ width: "2ch" }}>{barra.nota}</span>
            <span
              aria-hidden="true"
              style={{
                display: "inline-block",
                height: "0.75rem",
                width: `${Math.round(barra.proporcao * 12)}rem`,
                background: "var(--cor-marca, currentColor)",
                borderRadius: "2px",
              }}
            />
            <span>{barra.quantidade}</span>
          </li>
        ))}
      </ul>

      {resultado.por_mes.length > 0 ? (
        <Tabela legenda={`${resultado.nome}: envios, respostas e indicador por mês.`}>
          <Cabecalho>
            <Linha>
              <CelulaCabecalho>Mês</CelulaCabecalho>
              <CelulaCabecalho>Enviados</CelulaCabecalho>
              <CelulaCabecalho>Respondidos</CelulaCabecalho>
              <CelulaCabecalho>{resultado.tipo === "nps" ? "NPS" : "CES"}</CelulaCabecalho>
            </Linha>
          </Cabecalho>
          <Corpo>
            {resultado.por_mes.map((ponto) => (
              <Linha key={ponto.mes}>
                <Celula>{ponto.mes}</Celula>
                <Celula>{ponto.enviados}</Celula>
                <Celula>{ponto.respondidos}</Celula>
                <Celula>{indicadorLegivel(resultado.tipo, ponto.indicador)}</Celula>
              </Linha>
            ))}
          </Corpo>
        </Tabela>
      ) : null}

      {resultado.comentarios.length > 0 ? (
        <>
          <h3 className="titulo-secao">O que disseram</h3>
          <ul data-testid="comentarios">
            {resultado.comentarios.map((comentario, indice) => (
              <li key={indice} className="mb-2">
                <b>Nota {comentario.nota}</b> · {comentario.organizacao_nome}
                {comentario.numero_do_chamado ? ` · chamado #${comentario.numero_do_chamado}` : ""} ·{" "}
                {dataHora(comentario.respondido_em)}
                <p>{comentario.comentario}</p>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </Cartao>
  );
}

function Numero({
  rotulo,
  valor,
  detalhe,
  testid,
}: {
  rotulo: string;
  valor: string;
  detalhe?: string;
  testid: string;
}) {
  return (
    <div>
      <div className="celula-secundaria">{rotulo}</div>
      <div style={{ fontSize: "1.75rem", fontWeight: 700 }} data-testid={testid}>
        {valor}
      </div>
      {detalhe ? <div className="celula-secundaria">{detalhe}</div> : null}
    </div>
  );
}
